import { Link } from "react-router-dom";
import Seo, { localBusinessSchema, breadcrumbSchema } from "../components/Seo";
import { Reveal, Overline, FrameReveal } from "../components/Reveal";
import CtaBand from "../components/CtaBand";
import { useSettings, useAreas } from "../lib/hooks";
import { TEAM_IMG } from "../lib/site";

const STANDARDS = [
  ["Workmanship", "We put our name to every project, and we guarantee our workmanship for ten years. That only makes sense if the standard is genuinely high — so it is."],
  ["Honesty", "If something is not worth doing, or costs more than it should, or we are not the right builder for it, we will say so. Long-term reputation beats short-term wins."],
  ["Tidiness", "A clean site is a safe site and a considerate one. We protect your home, manage dust and waste, and leave things orderly at the end of every day."],
  ["Communication", "Regular, honest updates as standard. You will never have to chase us to find out what is happening on your own home."],
];

export default function About() {
  const { data: settings } = useSettings();
  const { data: areas } = useAreas();

  return (
    <div data-testid="about-page">
      <Seo
        title="About NOOR Design & Build | Owner-Led Builders Bristol"
        description="Owner-led residential construction company serving Bristol, Bath, Somerset and the surrounding areas — hands-on, honest advice."
        path="/about"
        schema={{ "@context": "https://schema.org", "@graph": [localBusinessSchema(settings), breadcrumbSchema([{ name: "Home", path: "/" }, { name: "About", path: "/about" }])] }}
      />
      <section className="bg-graphite text-white blueprint-grid">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 pt-32 md:pt-44 pb-16 md:pb-24">
          <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
            <div>
              <Overline light>[ About NOOR ]</Overline>
              <h1 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-6 max-w-3xl leading-[1.05]">
                A building company built on reputation, one project at a time.
              </h1>
            </div>
            <div className="border border-white/15 p-2">
              <FrameReveal src={TEAM_IMG} alt="Architectural construction drawings on a workbench" className="aspect-[4/3]" hoverZoom={false} eager testid="about-hero-image" />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-cream">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-16 md:py-28">
          <Reveal>
            <div className="grid lg:grid-cols-12 gap-8 pb-14 md:pb-20">
              <div className="lg:col-span-4">
                <p className="font-mono text-xs text-ember">[ 01 ]</p>
                <h2 className="font-display font-light text-3xl md:text-4xl tracking-tight mt-4">Our Story</h2>
              </div>
              <div className="lg:col-span-8 text-graphite/70 leading-relaxed space-y-5 text-sm md:text-base">
                <p>
                  NOOR Design &amp; Build is a growing, owner-led residential construction company serving Bristol,
                  Somerset and the surrounding areas. It was founded on a straightforward observation: most homeowners
                  planning a serious project are not looking for the cheapest price — they are looking for a
                  builder they can actually trust with their home.
                </p>
                <p>
                  We remain deliberately hands-on. The person who visits your home and prices your project is
                  involved in delivering it, and the tradespeople on your job are people we know, rate and have
                  worked with repeatedly. That is how quality stays consistent — and it is why we take on a
                  limited number of projects at a time.
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal>
            <div className="grid lg:grid-cols-12 gap-8 py-14 md:py-20 border-t border-black/10">
              <div className="lg:col-span-4">
                <p className="font-mono text-xs text-ember">[ 02 ]</p>
                <h2 className="font-display font-light text-3xl md:text-4xl tracking-tight mt-4">Our Standards</h2>
              </div>
              <div className="lg:col-span-8 grid sm:grid-cols-2 gap-px bg-black/10 border border-black/10">
                {STANDARDS.map(([t, x]) => (
                  <div key={t} className="bg-cream p-8">
                    <h3 className="font-display font-medium text-lg">{t}</h3>
                    <p className="text-sm text-graphite/60 leading-relaxed mt-3">{x}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal>
            <div className="grid lg:grid-cols-12 gap-8 py-14 md:py-20 border-t border-black/10">
              <div className="lg:col-span-4">
                <p className="font-mono text-xs text-ember">[ 03 ]</p>
                <h2 className="font-display font-light text-3xl md:text-4xl tracking-tight mt-4">Our Approach</h2>
              </div>
              <div className="lg:col-span-8 text-graphite/70 leading-relaxed space-y-5 text-sm md:text-base">
                <p>
                  We investigate before we price, we specify before we build, and we communicate throughout.
                  Every project begins with a proper site visit and ends with a proper handover — certificates,
                  guarantees and a walkthrough included.
                </p>
                <p>
                  We build for the long term: homes we are happy to drive past in ten years, and clients who are
                  happy to recommend us to their neighbours. Most of our work comes from exactly that.
                </p>
                <Link to="/how-we-work" data-testid="about-hww-link" className="inline-flex items-center gap-2 text-sm font-semibold text-graphite hover:text-ember transition-colors duration-300 underline underline-offset-4">
                  Read how we work in detail
                </Link>
              </div>
            </div>
          </Reveal>

          <Reveal>
            <div className="grid lg:grid-cols-12 gap-8 py-14 md:py-20 border-t border-black/10">
              <div className="lg:col-span-4">
                <p className="font-mono text-xs text-ember">[ 04 ]</p>
                <h2 className="font-display font-light text-3xl md:text-4xl tracking-tight mt-4">Our Guarantee</h2>
              </div>
              <div className="lg:col-span-8 text-graphite/70 leading-relaxed space-y-5 text-sm md:text-base">
                <p>
                  Every NOOR project carries a 10-year workmanship guarantee, and the company is fully insured.
                  When statutory sign-off applies — Building Regulations, structural certification, electrical
                  certification — it is obtained as a matter of course and handed to you at completion.
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal>
            <div className="grid lg:grid-cols-12 gap-8 py-14 md:py-20 border-t border-black/10">
              <div className="lg:col-span-4">
                <p className="font-mono text-xs text-ember">[ 05 ]</p>
                <h2 className="font-display font-light text-3xl md:text-4xl tracking-tight mt-4">Meet the Team</h2>
              </div>
              <div className="lg:col-span-8">
                <div className="grid md:grid-cols-2 gap-6 items-center">
                  <div className="border border-black/10 p-2">
                    <img
                      src={TEAM_IMG}
                      alt="Architectural construction drawings on a site workbench"
                      className="w-full aspect-[4/5] object-cover"
                      loading="lazy"
                      data-testid="about-van-image"
                    />
                  </div>
                  <div className="border border-dashed border-black/20 p-8" data-testid="team-placeholder">
                    <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-ember mb-4">
                      The NOOR van on site — you will know when we arrive
                    </p>
                    <p className="text-graphite/60 leading-relaxed text-sm md:text-base">
                      Team profiles and photographs are being prepared and will appear here shortly. What we can
                      tell you now: NOOR is owner-led, the leadership is on site rather than behind a desk, and
                      every tradesperson on a NOOR project is someone we would happily have in our own homes.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal>
            <div className="grid lg:grid-cols-12 gap-8 pt-14 md:pt-20 border-t border-black/10">
              <div className="lg:col-span-4">
                <p className="font-mono text-xs text-ember">[ 06 ]</p>
                <h2 className="font-display font-light text-3xl md:text-4xl tracking-tight mt-4">Areas We Serve</h2>
              </div>
              <div className="lg:col-span-8">
                <p className="text-graphite/70 leading-relaxed text-sm md:text-base max-w-2xl">
                  {settings?.primary_area || "Bristol, Bath, Somerset and the surrounding areas"}. Larger projects outside the
                  standard area may be considered following an initial consultation.
                </p>
                <div className="flex flex-wrap gap-2 mt-6">
                  {(areas || []).map((a) => (
                    <Link key={a.slug} to={`/areas-we-cover/${a.slug}`} data-testid={`about-area-${a.slug}`}
                      className="px-4 py-2 border border-black/15 hover:border-ember hover:text-ember text-xs font-medium tracking-wide transition-colors duration-300">
                      {a.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
      <CtaBand />
    </div>
  );
}
